/**
 * Team Evaluations Card
 *
 * Card component for team dashboard showing recent evaluations overview.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth';
import { useEvaluations } from '../evaluations/useEvaluations';
import { useTeamMembers } from './useTeam';
import type { EvaluationStatus, Evaluation } from '../../lib/evaluationsApi';

const STATUS_CONFIG: Record<EvaluationStatus, { label: string; color: string }> = {
  draft: { label: 'Concept', color: 'bg-yellow-100 text-yellow-800' },
  submitted: { label: 'Ingediend', color: 'bg-blue-100 text-blue-800' },
  in_review: { label: 'In Review', color: 'bg-purple-100 text-purple-800' },
  final: { label: 'Definitief', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Geannuleerd', color: 'bg-gray-100 text-gray-600' },
};

// Extended evaluation type with employee info
interface EvaluationWithEmployee extends Evaluation {
  employeeName?: string;
}

interface TeamEvaluationsCardProps {
  onCreateNew?: () => void;
}

export function TeamEvaluationsCard({ onCreateNew }: TeamEvaluationsCardProps) {
  const { user } = useAuth();
  const { data, isLoading: evalLoading, error } = useEvaluations({ managerId: user?.id });
  const { data: teamMembers, isLoading: teamLoading } = useTeamMembers();

  const isLoading = evalLoading || teamLoading;

  // Map employee IDs to names using team members data
  const employeeMap = useMemo(() => {
    const map = new Map<number, string>();
    teamMembers?.forEach((member) => {
      map.set(member.employeeId, member.name);
    });
    return map;
  }, [teamMembers]);

  // Add employee names to evaluations
  const evaluations: EvaluationWithEmployee[] = useMemo(() => {
    return (data?.data || []).map((e) => ({
      ...e,
      employeeName: employeeMap.get(e.employeeId),
    }));
  }, [data?.data, employeeMap]);

  // Count evaluations by status
  const statusCounts = {
    draft: evaluations.filter((e) => e.status === 'draft').length,
    submitted: evaluations.filter((e) => e.status === 'submitted' || e.status === 'in_review').length,
    final: evaluations.filter((e) => e.status === 'final').length,
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Beoordelingen</h3>
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Beoordelingen</h3>
        <p className="text-red-600 text-sm">Kon data niet laden.</p>
      </div>
    );
  }

  // Get recent evaluations (max 5)
  const recentEvaluations = [...evaluations]
    .sort((a, b) => new Date(b.periodeStart).getTime() - new Date(a.periodeStart).getTime())
    .slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-900">Team Beoordelingen</h3>
        {onCreateNew && (
          <button
            onClick={onCreateNew}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
            title="Nieuwe beoordeling starten"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuw
          </button>
        )}
      </div>
      {/* Status badges */}
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          {statusCounts.draft} concept
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {statusCounts.submitted} ingediend
        </span>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          {statusCounts.final} afgerond
        </span>
      </div>

      {recentEvaluations.length > 0 ? (
        <div className="space-y-3">
          {recentEvaluations.map((evaluation) => (
            <Link
              key={evaluation.id}
              to={`/beoordeling/${evaluation.id}/wizard`}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {evaluation.employeeName || `Medewerker #${evaluation.employeeId}`}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[evaluation.status]?.color || 'bg-gray-100'}`}>
                    {STATUS_CONFIG[evaluation.status]?.label || evaluation.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })}
                  </span>
                </div>
              </div>
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          ))}

          <Link
            to="/team/beoordelingen"
            className="block text-center text-sm text-blue-600 hover:text-blue-800 py-2"
          >
            Bekijk alle {evaluations.length} beoordelingen →
          </Link>
        </div>
      ) : (
        <div className="text-center py-6">
          <svg
            className="w-12 h-12 text-gray-300 mx-auto mb-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-gray-500 text-sm">
            Nog geen beoordelingen voor je team.
          </p>
          <Link
            to="/team/beoordelingen"
            className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800"
          >
            Bekijk overzicht →
          </Link>
        </div>
      )}
    </div>
  );
}
